import * as THREE from 'three';
import { PuzzleElement } from './PuzzleElement';

export class ExitPortal extends PuzzleElement {
  private portalRadius: number = 1.5;
  private isActivated: boolean = false;
  private particles: THREE.Points;
  private particlePositions: Float32Array;
  private onEnter: (() => void) | null = null;
  
  constructor(position: THREE.Vector3, onEnter?: () => void) {
    super(position);
    this.onEnter = onEnter || null;
    
    // Create particle effect
    const particleCount = 100;
    this.particlePositions = new Float32Array(particleCount * 3);
    
    const particleGeometry = new THREE.BufferGeometry();
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const radius = this.portalRadius + Math.random() * 0.5;
      this.particlePositions[i * 3] = Math.cos(angle) * radius;
      this.particlePositions[i * 3 + 1] = Math.random() * 3;
      this.particlePositions[i * 3 + 2] = Math.sin(angle) * radius;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffd700,
      size: 0.1,
      transparent: true,
      opacity: 0.8
    });
    
    this.particles = new THREE.Points(particleGeometry, particleMaterial);
    this.particles.position.copy(position);
    this.mesh.add(this.particles);
  }
  
  protected createMesh(): THREE.Group {
    const group = new THREE.Group();
    
    // Portal ring
    const ringGeometry = new THREE.TorusGeometry(this.portalRadius, 0.1, 8, 32);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      emissive: 0xffd700,
      emissiveIntensity: 0.5,
      metalness: 0.8,
      roughness: 0.2
    });
    
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
    
    // Portal surface
    const portalGeometry = new THREE.CircleGeometry(this.portalRadius - 0.1, 32);
    const portalMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffcc,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide
    });
    
    const portal = new THREE.Mesh(portalGeometry, portalMaterial);
    portal.rotation.x = Math.PI / 2;
    portal.position.y = 0.01;
    group.add(portal);
    
    // Light
    const light = new THREE.PointLight(0xffd700, 2, 10);
    light.position.y = 1;
    group.add(light);
    
    return group;
  }
  
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    // Rotate particles
    this.particles.rotation.y += deltaTime * 0.5;
    
    // Animate particle positions
    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < positions.length / 3; i++) {
      positions[i * 3 + 1] += deltaTime * 2;
      if (positions[i * 3 + 1] > 3) {
        positions[i * 3 + 1] = 0;
      }
    }
    this.particles.geometry.attributes.position.needsUpdate = true;
    
    // Check if player is on portal
    const distance = new THREE.Vector2(
      playerPosition.x - this.position.x,
      playerPosition.z - this.position.z
    ).length();
    
    if (distance < this.portalRadius && !this.isActivated) {
      this.activate();
    }
  }
  
  private activate(): void {
    this.isActivated = true;
    this.onEnter?.();
  }
  
  public setOnEnter(callback: () => void): void {
    this.onEnter = callback;
  }
  
  public isActivatedState(): boolean {
    return this.isActivated;
  }
}
